/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {Assets, Devices} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';

// defines the enum for move supported modules ...
export enum MovedAsset {
  Asset = 'Asset',
  Device = 'Device'
};

/**
 * Provides a utility which process the provided moved devices and errors and returns
 * the formated array so that the same can be displayed on the grid.
 *
 * ```typescript
 *
 *   import {getMovedDevicesResults} from '@ion/dls/src/editors/MovedEntitiesEditorUtils';
 *
 *   const devices = getMovedDevicesResults(movedDevices, errors);
 *
 * ```
 */
export const getMovedDevicesResults = (moved: Devices, errors: ServiceError[]) => {
  const results = moved?.map(device => ({
    ...device,
    identifier : device?.esn,
    response   : 'Success',
    code       : 200
  }));
  const failed = errors?.map(error => {
    if (error) {
      const {data, errorCode, errorText} = error;
      const device = safeJSONParse(data)?.device;
      return {
        ...(device || {}),
        identifier : device?.esn,
        code       : errorCode,
        response   : errorText?.replace(/(?:\r\n|\r|\n|,)/g, '')
      };
    }
  });
  return [...results, ...failed];
};

/**
 * Provides a utility which process the provided assets and errors and returns
 * the formated array so that the same can be displayed on the grid.
 *
 * ```typescript
 *
 *   import {getMovedAssetsResults} from '@ion/dls/src/editors/MovedEntitiesEditorUtils';
 *
 *   const assets = getMovedAssetsResults(movedAssets, errors);
 *
 * ```
 */
export const getMovedAssetsResults = (moved: Assets, errors: ServiceError[]) => {
  const results = moved?.map(asset => ({
    ...asset,
    identifier : formatDateTime(asset?.lastModifiedOn),
    response   : 'Success',
    code       : 200
  }));
  const failed = errors?.map((error) => {
    if (error) {
      const {data, errorCode, errorText} = error;
      const asset = safeJSONParse(data)?.asset;
      return {
        ...(asset || {}),
        identifier : formatDateTime(asset?.lastModifiedOn),
        code       : errorCode,
        response   : errorText?.replace(/(?:\r\n|\r|\n|,)/g, '')
      };
    }
  });
  return [...results, ...failed];
};

const resultsMap = {
  [MovedAsset.Asset]  : getMovedAssetsResults,
  [MovedAsset.Device] : getMovedDevicesResults
};

/**
 * Provides a utility which returns the entity specific result function
 * checking through the above resultsMap object.
 *
 * ```typescript
 *
 *   import {getResults} from '@ion/dls/src/editors/MovedEntitiesEditorUtils';
 *
 *   const results = getResults(entity, movedAssets, errors);
 *
 * ```
 */
export const getResults = (entity: string, moved: Devices | Assets, errors: ServiceError[]) => {
  const key = {
    [translate('common:entityLabels.device')] : MovedAsset.Device,
    [translate('common:entityLabels.asset')]  : MovedAsset.Asset
  }[entity] ?? entity;
  return (resultsMap[key])?.(moved, errors);
};
